/**
 * Follow-up drawer — the next follow-up date (drawer redesign Phase 6a, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-drawer-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Nilay 2026-10-02: "Today's rules, design's look." Claims, pre-auths and balances keep an editable
 * date; the other queues show the office-frequency date, with Override wherever today allows it.
 *
 * ── SOURCE OF TRUTH: TODAY'S DRAWER ────────────────────────────────────────────────────────────
 * CreateFollowup.vue (FROZEN): calculateNextFollowupDate (:2417), getAutoFrequency (:2724),
 * updateFollowupNote's date half (:2462–2471), followupDateEditable (:3084), the Override / Cancel
 * links (template :444–475) and doOverride / cancelOverride (:1834, :1941).
 *
 *   frequency   the queue's AUTO frequency setting ("2 weeks") when set and non-blank; otherwise the
 *               per-attempt CUSTOM setting <PREFIX><attempt number> ("1 days"). On claims, pre-auths
 *               and balances a missing custom setting defaults to 1 WEEK; anywhere else it throws.
 *   date        today + frequency, in calendar days / weeks / months (moment's add: a month clamps
 *               to the month's last day; a fraction rounds half away from zero; an unknown unit or a
 *               non-number adds nothing).
 *   last attempt  no date — the field is hidden and the save sends FollowupDate null.
 *   status       a status whose TeamioFollowupStatusID is 1 (appointment scheduled), 5 (claim
 *               processed) or 3 (no follow-up needed) hides the date and sends null; picking any other
 *               status re-computes it on recall / unscheduled / broken unless overridden.
 *   editable     claims, outstanding pre-auths, balances: a date picker, no Override link.
 *   override     every other queue (processed pre-auths included): Override shows the picker and
 *               the save sends Overridden: 1; Cancel restores the date from before the override.
 *
 * ⚠ Settings reads that THROW today (legacyError below): claims read
 * OUTSTANDING_CLAIM_FOLLOWUP_DURATION_TYPE unguarded (:2732); a missing custom per-attempt setting
 * on a patient queue (:2442). When it throws, today's form keeps its previous date (the moment the
 * drawer opened, i.e. today) and a status change never writes the follow-up note.
 *
 * ── FIXED IN THE NEW DRAWER (Nilay 2026-10-02, drawer plan "6a notes") ───────────────────────
 *   bug 1  broken appointments read the auto frequency from BROKEN_APPOINTMENTS_AUTO_FREQUENCY, the
 *          key Settings.vue writes (today: BROKEN_APPOINTMENTS_FREQUENCY, which throws — :2728).
 *          drawerQueues carries the key; frequencyOf reads it like every other queue.
 *   bug 6  the LAST attempt keeps "no next date" after a status pick on every queue. Today the
 *          queues that don't re-compute on a status change (claims, pre-auths, balances) bring the
 *          date field back EMPTY (:2468) and the save is refused until a date is picked —
 *          processed pre-auths only through Override. dateAfterStatusChange returns hidden / null.
 */

const MONEY = ['AgingDetails', 'OutstandingPreAuthDetails', 'PatientBalancesDetails'];
const RECALC_ON_STATUS = ['OverdueRecallDetails', 'unscheduledPatientDetails', 'BrokenAppointmentDetails'];

/** TeamioFollowupStatusID values that end the follow-up's date (:3348–3363). */
export const NO_DATE_STATUS = { appointmentScheduled: 1, followupNotRequired: 3, claimProcessed: 5 };

/** The money queues' default when the per-attempt setting is missing (:2437–2438). */
export const MONEY_DEFAULT_FREQUENCY = { count: 1, unit: 'WEEK' };

export function isDateEditable(detailPageName) {
  return MONEY.indexOf(detailPageName) !== -1;
}

/** The Override link shows (template :466): not already overridden, and not a money queue. */
export function canOverride(detailPageName, overridden) {
  return !overridden && !isDateEditable(detailPageName);
}

/** The picker shows when the date is editable or overridden; otherwise a read-only date (template :448, :461). */
export function datePickerShown(detailPageName, overridden) {
  return isDateEditable(detailPageName) || !!overridden;
}

/** What a save sends as `Overridden` (:2081). Picking a date on a money queue is NOT an override. */
export function overriddenFlag(overridden) {
  return overridden ? 1 : 0;
}

function pv(allSettings, key) {
  const row = allSettings ? allSettings[key] : undefined;
  return row ? row.PropertyValue : undefined;
}

/**
 * [getAutoFrequency + calculateNextFollowupDate's frequency half] → { count, unit, source } or
 * { legacyError: { setting } } where today's drawer throws. `queue` = drawerQueues' entry.
 */
export function frequencyOf({ queue, allSettings, totalAttempts } = {}) {
  if (!queue || !allSettings) return { legacyError: { setting: null } };
  const s = queue.settings;
  let auto;
  if (queue.detailPageName === 'AgingDetails') {
    const duration = allSettings[s.autoFrequencyDuration];
    if (duration && duration !== '') {
      if (!allSettings[s.autoFrequencyDurationType]) return { legacyError: { setting: s.autoFrequencyDurationType } };
      auto = `${duration.PropertyValue} ${allSettings[s.autoFrequencyDurationType].PropertyValue}`;
    } else {
      auto = '';
    }
  } else if (allSettings[s.autoFrequencyGuard]) {
    if (!allSettings[s.autoFrequency]) return { legacyError: { setting: s.autoFrequency } };
    auto = allSettings[s.autoFrequency].PropertyValue;
  } else {
    auto = '';
  }

  if (auto !== null && auto !== '') {
    if (typeof auto !== 'string') return { legacyError: { setting: s.autoFrequency } };
    return { count: auto.split(' ')[0], unit: auto.split(' ')[1], source: 'auto' };
  }

  const key = `${s.customFrequencyPrefix}${totalAttempts + 1}`;
  const value = pv(allSettings, key);
  if (MONEY.indexOf(queue.detailPageName) !== -1) {
    if (allSettings[key]) return { count: value.split(' ')[0], unit: value.split(' ')[1], source: 'custom', setting: key };
    return { count: MONEY_DEFAULT_FREQUENCY.count, unit: MONEY_DEFAULT_FREQUENCY.unit, source: 'default', setting: key };
  }
  if (!allSettings[key]) return { legacyError: { setting: key } };
  return { count: value.split(' ')[0], unit: value.split(' ')[1], source: 'custom', setting: key };
}

const UNIT = {
  d: 'day', day: 'day', days: 'day',
  w: 'week', week: 'week', weeks: 'week',
  M: 'month', month: 'month', months: 'month',
  Q: 'quarter', quarter: 'quarter', quarters: 'quarter',
  y: 'year', year: 'year', years: 'year',
};

/** moment's unit aliases for the units a follow-up frequency can hold; anything else → null. */
export function normalizeUnit(unit) {
  if (typeof unit !== 'string') return null;
  return UNIT[unit] || UNIT[unit.toLowerCase()] || null;
}

function absRound(n) {
  return n < 0 ? Math.round(-n) * -1 : Math.round(n);
}

function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}

/** 'YYYY-MM-DD' + count units, as moment().add(count, unit).format('YYYY-MM-DD') would give. */
export function addToYmd(ymd, count, unit) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '');
  if (!m) return null;
  let y = Number(m[1]);
  let mo = Number(m[2]) - 1;
  let d = Number(m[3]);
  const u = normalizeUnit(unit);
  const n = typeof count === 'number' ? count : (count === '' || count === null || count === undefined ? 0 : Number(count));
  if (!u || !isFinite(n)) return ymd.slice(0, 10);
  let days = 0;
  let months = 0;
  if (u === 'day') days = absRound(n);
  else if (u === 'week') days = absRound(n * 7);
  else if (u === 'month') months = absRound(n);
  else if (u === 'quarter') months = absRound(n * 3);
  else if (u === 'year') months = absRound(n * 12);
  if (months) {
    const total = y * 12 + mo + months;
    y = Math.floor(total / 12);
    mo = total - y * 12;
    const last = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
    if (d > last) d = last;
  }
  const t = new Date(Date.UTC(y, mo, d + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/**
 * [calculateNextFollowupDate, :2417] → { visible, date, frequency, legacyError }.
 * Runs when a Phone / Text / Email form opens. `today` = 'YYYY-MM-DD' in the browser's zone.
 */
export function nextFollowupDate({ queue, allSettings, totalAttempts, isLastAttempt, today } = {}) {
  if (isLastAttempt) return { visible: false, date: null, frequency: null, legacyError: null };
  const f = frequencyOf({ queue, allSettings, totalAttempts });
  if (f.legacyError) return { visible: true, date: null, frequency: null, legacyError: f.legacyError };
  return { visible: true, date: addToYmd(today, f.count, f.unit), frequency: f, legacyError: null };
}

/**
 * [updateFollowupNote's date half, :2462] after the Phone form's status changes.
 * `status` = the chosen status row (its TeamioFollowupStatusID); `statusID` the chosen id (0 = none).
 * `currentDate` is what the form shows now. Recall / unscheduled / broken re-compute unless overridden;
 * the money queues and processed pre-auths keep whatever the form holds.
 */
export function dateAfterStatusChange({ queue, status, statusID, overridden, currentDate, allSettings, totalAttempts, isLastAttempt, today } = {}) {
  const t = status ? status.TeamioFollowupStatusID : undefined;
  const ends = t === NO_DATE_STATUS.appointmentScheduled
    || (statusID !== 0 && t === NO_DATE_STATUS.claimProcessed)
    || (statusID !== 0 && t === NO_DATE_STATUS.followupNotRequired);
  if (ends) return { visible: false, date: null, recalculated: false, legacyError: null };
  // bug 6 (fixed): the last attempt has no next date whatever status is picked — as when the form opened.
  if (isLastAttempt) return { visible: false, date: null, recalculated: false, legacyError: null };
  if (!overridden && queue && RECALC_ON_STATUS.indexOf(queue.detailPageName) !== -1) {
    const n = nextFollowupDate({ queue, allSettings, totalAttempts, isLastAttempt, today });
    if (n.legacyError) return { visible: true, date: currentDate, recalculated: false, legacyError: n.legacyError };
    return { visible: n.visible, date: n.date, recalculated: true, legacyError: null };
  }
  return { visible: true, date: currentDate, recalculated: false, legacyError: null };
}

/** The earliest date the picker allows (notBefore = the moment the drawer opened → today). */
export function minPickableDate(today) {
  return today;
}

/**
 * The design's line under the Phone outcome: 'stop' when the picked status ends follow-ups
 * (TeamioFollowupStatusID 3, "no follow-up needed"), 'done' when it closes them (1 appointment
 * scheduled, 5 claim processed) — the same statuses that hide the next date above. null otherwise.
 */
export function outcomeNoteKind(status) {
  const t = status ? status.TeamioFollowupStatusID : undefined;
  if (t === NO_DATE_STATUS.followupNotRequired) return 'stop';
  if (t === NO_DATE_STATUS.appointmentScheduled || t === NO_DATE_STATUS.claimProcessed) return 'done';
  return null;
}

/** The design's quick picks under an editable date: label + 'YYYY-MM-DD' from `today`. */
export const DATE_PRESETS = [
  { key: 'tomorrow', label: 'Tomorrow', count: 1, unit: 'day' },
  { key: 'in3', label: 'In 3 days', count: 3, unit: 'day' },
  { key: 'week', label: 'Next week', count: 1, unit: 'week' },
  { key: 'twoWeeks', label: 'In 2 weeks', count: 2, unit: 'week' },
  { key: 'month', label: 'In a month', count: 1, unit: 'month' },
];

export function datePresets(today) {
  return DATE_PRESETS.map(p => ({ key: p.key, label: p.label, date: addToYmd(today, p.count, p.unit) }));
}

/** Whole calendar days from `today` to `ymd` (both 'YYYY-MM-DD'), or null. */
export function daysFromToday(ymd, today) {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '');
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(today || '');
  if (!a || !b) return null;
  return Math.round((Date.UTC(+a[1], +a[2] - 1, +a[3]) - Date.UTC(+b[1], +b[2] - 1, +b[3])) / 864e5);
}
